import { NavLink, useLocation, useNavigate } from "react-router-dom";
import logo from "../../images/logo_horizontal.svg";
import UserEdit from "../other/UserEdit";
import Modal from "./Modal";
import { useAuth } from "../../context/useAuth";
import { useEffect, useState, useRef } from "react";
import { useVisibility } from "../../context/useVisibility";
import ReactDOM from "react-dom";
import classNames from "classnames";
import { confirm } from "./ConfirmModal";

function SidebarParentButton({ icon, label, isPinned, onPin, onExpand, onMouseEnter, onMouseLeave }) {
  return (
    <div className="sidebar-btn" onClick={onExpand} onMouseEnter={onMouseEnter} onMouseLeave={onMouseLeave}>
      <i className="material-icons">{icon}</i>
      <div className="sidebar-show">{label}</div>
      <i className="material-icons right-btn" style={{ visibility: "visible" }}>
        chevron_right
      </i>
      <i
        className={`material-icons pin-icon${isPinned ? " pinned" : ""}`}
        style={{ cursor: "pointer" }}
        onClick={(e) => {
          e.stopPropagation();
          onPin(e);
        }}
        title={isPinned ? "Unpin" : "Pin"}
      >
        push_pin
      </i>
    </div>
  );
}

function SidebarSubmenu({ items, isPinned, onPin, isActive }) {
  return (
    <div className="sidebar-submenu">
      {items.map((item) => (
        <NavLink key={item.label} className={`sidebar-btn${isActive(item.to) ? " active" : ""}`} to={item.to}>
          <div className="sidebar-show">{item.label}</div>
          <i
            className={`material-icons pin-icon${isPinned(item.to) ? " pinned" : ""}`}
            style={{ cursor: "pointer" }}
            onClick={(e) => {
              e.preventDefault();
              onPin(item.to);
            }}
            title={isPinned(item.to) ? "Unpin" : "Pin"}
          >
            push_pin
          </i>
        </NavLink>
      ))}
    </div>
  );
}

function SidebarPopover({ show, position, onMouseEnter, onMouseLeave, children, inverse = false, popoverRef }) {
  const [popoverHeight, setPopoverHeight] = useState(0);
  const localRef = useRef();
  const ref = popoverRef || localRef;

  useEffect(() => {
    if (ref.current) setPopoverHeight(ref.current.offsetHeight);
  }, [show, children, ref]);

  if (!show) return null;

  const style = {
    position: "fixed",
    top: inverse ? position.top - popoverHeight + position.height : position.top,
    left: position.left,
    zIndex: 100000,
  };

  return ReactDOM.createPortal(
    <div className="sidebar-popover" style={style} onMouseEnter={onMouseEnter} onMouseLeave={onMouseLeave} ref={ref}>
      {children}
    </div>,
    document.body
  );
}

export default function Sidebar({ sidebarVisible }) {
  const { checkPrivileges, logout, user } = useAuth();
  const { pinnedTabs, savePinnedTabs } = useVisibility();
  const [showProfileModal, setShowProfileModal] = useState(false);
  const [popover, setPopover] = useState(null);
  const [mobileSidebar, setMobileSidebar] = useState(false);
  const [expandedParent, setExpandedParent] = useState(null);

  const location = useLocation();
  const navigate = useNavigate();
  const [popoverPosition, setPopoverPosition] = useState({ top: 0, left: 0 });
  const triggerRef = useRef();
  const closeTimeout = useRef();

  const SETTINGS_SUBMENU = [
    {
      label: "Users",
      to: "/user",
      show: checkPrivileges("editUser"),
    },
    { label: "Roles", to: "/settings/roles", show: checkPrivileges("editRoles") },
  ];

  const SIDEBAR_ORDER = [
    { type: "item", key: "orgChart" },
    { type: "parent", key: "settings" },
  ];

  const SIDEBAR_CONFIG = {
    settings: {
      label: "Settings",
      icon: "settings",
      to: "/settings",
      submenu: SETTINGS_SUBMENU.filter((item) => item.show),
    },
    orgChart: {
      label: "Org Chart",
      icon: "account_tree",
      to: "/orgchart",
    },
  };

  // Close popover on outside click
  const popoverRef = useRef();
  useEffect(() => {
    function handleClick(e) {
      if (popoverRef.current && !popoverRef.current.contains(e.target)) setPopover(null);
    }
    if (popover) document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, [popover]);

  useEffect(() => {
    setPopover(null);
  }, [location]);

  // Close mobile sidebar on navigation
  useEffect(() => {
    setMobileSidebar(false);
  }, [location]);

  // Reset expanded parent on resize
  useEffect(() => {
    function handleResize() {
      const tabletSize = parseInt(getComputedStyle(document.documentElement).getPropertyValue("--tablet-size"), 10) || 768;
      if (window.innerWidth > tabletSize) {
        setExpandedParent(null);
        setMobileSidebar(false);
      }
    }
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Helpers
  const isActive = (to) => {
    if (to === "/orgchart") {
      return location.pathname === "/" || location.pathname === "/orgchart";
    }
    return location.pathname === to || location.pathname.startsWith(to);
  };

  const isPinned = (to) => pinnedTabs.includes(to);
  const togglePin = (to) =>
    isPinned(to) ? savePinnedTabs(pinnedTabs.filter((tab) => tab !== to)) : savePinnedTabs([...pinnedTabs, to]);

  const allMenuItems = [
    ...SIDEBAR_ORDER.map(({ key, type }) => {
      const { label, to, icon, submenu } = SIDEBAR_CONFIG[key];
      return { label, to, icon, isParent: type === "parent", submenu };
    }),
    ...SETTINGS_SUBMENU.filter((i) => i.show).map((i) => ({ ...i, parent: "settings" })),
  ];

  // Popover helpers
  const handlePopover = (type) => (e) => {
    clearTimeout(closeTimeout.current);
    setPopover(type);
    const rect = e.currentTarget.getBoundingClientRect();
    setPopoverPosition({ top: rect.top, left: rect.right, height: rect.height });
    triggerRef.current = e.currentTarget;
  };
  const handlePopoverClose = () => (closeTimeout.current = setTimeout(() => setPopover(null), 100));
  const handlePopoverEnter = () => clearTimeout(closeTimeout.current);

  // Confirm before logout
  const handleLogout = async (e) => {
    e.preventDefault();
    const confirmed = await confirm({
      title: "Confirm Logout",
      message: "Are you sure you want to log out?",
    });
    if (confirmed) logout();
  };

  // If normalization changed the tabs, update backend
  useEffect(() => {
    if (pinnedTabs.length > 0) {
      savePinnedTabs(pinnedTabs);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(pinnedTabs)]);

  return (
    <div className={classNames("sidebar", sidebarVisible ? "show" : "hide", mobileSidebar && "show-mobile")}>
      {" "}
      <div className="sidebar-logo">
        <button className="btn-sidebar-mobile" onClick={() => setMobileSidebar((prev) => !prev)} aria-label="Open sidebar">
          <i className="material-icons">menu</i>
        </button>
        <NavLink to="/">
          <div className="logo-wrapper">
            <img className="sidebar-logo-img" src={logo} alt="Logo" />
            <div className="logo-text">Business Operating System</div>
          </div>
        </NavLink>
      </div>
      <div className="sidebar-delimiter"></div>
      <div className="sidebar-scrollable">
        {pinnedTabs.length > 0 && (
          <>
            <div className="sidebar-pinned">
              <div className="sidebar-pinned-title">PINNED</div>
              {pinnedTabs.map((to) => {
                const item = allMenuItems.find((i) => i.to === to);
                if (!item) return null;
                const isParent = !!item.isParent;
                if (isParent) {
                  const parentConfig = SIDEBAR_ORDER.find((e) => SIDEBAR_CONFIG[e.key]?.to === item.to);
                  const key = `pinned-${parentConfig.key}`;
                  const submenu = SIDEBAR_CONFIG[parentConfig.key].submenu;
                  const firstSubmenuItem = submenu && submenu[0];
                  return (
                    <div key={item.to}>
                      <SidebarParentButton
                        icon={item.icon}
                        label={item.label}
                        isPinned={true}
                        onPin={(e) => {
                          e.preventDefault();
                          togglePin(item.to);
                        }}
                        onExpand={(e) => {
                          e.preventDefault();
                          if (mobileSidebar) {
                            setExpandedParent(expandedParent === key ? null : key);
                          } else if (firstSubmenuItem) {
                            navigate(firstSubmenuItem.to);
                          }
                        }}
                        onMouseEnter={!mobileSidebar ? handlePopover(parentConfig.key) : undefined}
                        onMouseLeave={!mobileSidebar ? handlePopoverClose : undefined}
                      />
                      {mobileSidebar && expandedParent === key && (
                        <SidebarSubmenu
                          items={SIDEBAR_CONFIG[parentConfig.key].submenu}
                          isPinned={isPinned}
                          onPin={togglePin}
                          isActive={isActive}
                        />
                      )}
                    </div>
                  );
                }
                // Not a parent
                return (
                  <NavLink key={item.to} className={({ isActive }) => `sidebar-btn${isActive ? " active" : ""}`} to={item.to}>
                    <div className="sidebar-show">{item.label}</div>
                    <i className="material-icons right-btn" style={{ visibility: "hidden" }}>
                      chevron_right
                    </i>
                    <i
                      className="material-icons pin-icon pinned"
                      style={{ cursor: "pointer" }}
                      onClick={(e) => {
                        e.preventDefault();
                        togglePin(item.to);
                      }}
                      title="Unpin"
                    >
                      push_pin
                    </i>
                  </NavLink>
                );
              })}
            </div>
            <div className="sidebar-delimiter"></div>
          </>
        )}

        <div className="sidebar-menu">
          {SIDEBAR_ORDER.map((entry) => {
            const cfg = SIDEBAR_CONFIG[entry.key];
            if (!cfg) return null;
            if (entry.type === "parent") {
              const firstSubmenuItem = cfg.submenu && cfg.submenu[0];
              return (
                <div key={entry.key}>
                  <SidebarParentButton
                    icon={cfg.icon}
                    label={cfg.label}
                    isPinned={isPinned(cfg.to)}
                    onPin={(e) => {
                      e.preventDefault();
                      togglePin(cfg.to);
                    }}
                    onExpand={(e) => {
                      e.preventDefault();
                      if (mobileSidebar) {
                        setExpandedParent(expandedParent === entry.key ? null : entry.key);
                      } else if (firstSubmenuItem) {
                        navigate(firstSubmenuItem.to);
                      }
                    }}
                    onMouseEnter={!mobileSidebar ? handlePopover(entry.key) : undefined}
                    onMouseLeave={!mobileSidebar ? handlePopoverClose : undefined}
                  />
                  {mobileSidebar && expandedParent === entry.key && (
                    <SidebarSubmenu items={cfg.submenu} isPinned={isPinned} onPin={togglePin} isActive={isActive} />
                  )}
                </div>
              );
            }
            // Single item
            if (cfg.show === false) return null;
            return (
              <NavLink key={entry.key} className={`sidebar-btn${isActive(cfg.to) ? " active" : ""}`} to={cfg.to}>
                <i className="material-icons">{cfg.icon}</i>
                <div className="sidebar-show">{cfg.label}</div>
                <i className="material-icons right-btn" style={{ visibility: "hidden" }}>
                  chevron_right
                </i>
                <i
                  className={`material-icons pin-icon${isPinned(cfg.to) ? " pinned" : ""}`}
                  style={{ cursor: "pointer" }}
                  onClick={(e) => {
                    e.preventDefault();
                    togglePin(cfg.to);
                  }}
                  title={isPinned(cfg.to) ? "Unpin" : "Pin"}
                >
                  push_pin
                </i>
              </NavLink>
            );
          })}
        </div>
      </div>
      <div className="sidebar-delimiter"></div>
      <div className="sidebar-bottom">
        <div className="sidebar-bottom-left">
          <div className="user-badge" onClick={() => setShowProfileModal(true)}>
            {user?.firstName?.charAt(0)}
            {user?.lastName?.charAt(0)}
          </div>
        </div>
        <div className="sidebar-bottom-right">
          <div className="sidebar-bottom-user" onClick={() => setShowProfileModal(true)}>
            <div className="sidebar-bottom-name">
              {user?.firstName} {user?.lastName}
            </div>
            <div className="sidebar-bottom-email">{user?.email}</div>
          </div>
          <NavLink className="sidebar-btn not-active" to="/" onClick={handleLogout}>
            <i className="material-icons">logout</i>
          </NavLink>
        </div>
      </div>
      {showProfileModal && (
        <Modal close={() => setShowProfileModal(false)}>
          <UserEdit
            userInit={user}
            list={[]}
            setList={() => {}}
            index={0}
            close={() => setShowProfileModal(false)}
            readOnlyFields
          />
        </Modal>
      )}
      {/* Popovers */}
      {SIDEBAR_ORDER.filter((e) => e.type === "parent").map((entry) => {
        const cfg = SIDEBAR_CONFIG[entry.key];

        let dynamicInverse = false;
        if (popover === entry.key && cfg?.submenu?.length) {
          const estimatedHeight = cfg.submenu.length * 48;
          const popoverBottom = popoverPosition.top + estimatedHeight;
          if (popoverBottom > window.innerHeight) {
            dynamicInverse = true;
          }
        }
        return (
          <SidebarPopover
            key={entry.key}
            show={popover === entry.key}
            position={popoverPosition}
            onMouseEnter={handlePopoverEnter}
            onMouseLeave={handlePopoverClose}
            popoverRef={popoverRef}
            inverse={dynamicInverse}
          >
            {cfg.submenu.map((item) => (
              <NavLink key={item.label} className={`sidebar-btn${isActive(item.to) ? " active" : ""}`} to={item.to}>
                <div className="sidebar-show">{item.label}</div>
                <i
                  className={`material-icons pin-icon${isPinned(item.to) ? " pinned" : ""}`}
                  style={{ marginLeft: "auto", cursor: "pointer" }}
                  onClick={(e) => {
                    e.preventDefault();
                    togglePin(item.to);
                  }}
                  title={isPinned(item.to) ? "Unpin" : "Pin"}
                >
                  push_pin
                </i>
              </NavLink>
            ))}
          </SidebarPopover>
        );
      })}
    </div>
  );
}
